import { useLocation } from "react-router-dom";
import { useSharedStatus } from "../lib/StatusContext";
import { serviceUrl } from "../lib/links";
import { useHashTarget } from "../lib/motion";
import { TopbarSettings } from "../components/TopbarSettings";
import { LibraryRow, PageSkeleton, Section, SI } from "../components/status/atoms";

function splitSpeed(text: string): { n: string; unit: string } {
  const m = /^([\d.]+)\s*(.*)$/.exec(text.trim());
  return m?.[1] ? { n: m[1], unit: m[2] || "MB/s" } : { n: text || "0", unit: "MB/s" };
}

function etaMin(etaText: string): string {
  const parts = etaText.split(":").map(Number);
  if (parts.length === 3 && parts.every((n) => Number.isFinite(n))) {
    const mins = Math.max(0, Math.round(parts[0]! * 60 + parts[1]! + parts[2]! / 60));
    return `${mins} MIN`;
  }
  if (parts.length === 2 && parts.every((n) => Number.isFinite(n))) {
    return `${Math.max(0, parts[0]!)} MIN`;
  }
  return etaText ? etaText.toUpperCase() : "—";
}

export function DownloadsView() {
  const { hash } = useLocation();
  const flashing = useHashTarget(hash);
  const { data, error, loading, reload } = useSharedStatus();

  if (loading && !data) return <PageSkeleton title="Downloads" />;

  const sabUrl = serviceUrl(data?.services.find((s) => s.id === "downloads")?.url);
  const speed = splitSpeed(data?.downloads.speedText ?? "0 MB/s");
  const jobs = data?.downloads.jobs ?? [];
  const overall = jobs.length
    ? Math.round(jobs.reduce((n, j) => n + j.percent, 0) / jobs.length)
    : data?.downloads.queueCount
      ? 0
      : 100;
  const live = Boolean(data?.downloads.ok && jobs.some((j) => /download/i.test(j.status)));

  return (
    <div className="page">
      <header className="topbar">
        <h1 className="topbar__title">Downloads</h1>
        <div className="topbar__right">
          <button
            type="button"
            className={`btn btn--ghost btn--icon ${loading ? "is-spinning" : ""}`}
            onClick={() => void reload()}
            aria-label="Refresh"
          >
            <svg {...SI} width="19" height="19">
              <path d="M21 12a9 9 0 1 1-2.6-6.4M21 3v6h-6" />
            </svg>
          </button>
          <TopbarSettings />
        </div>
      </header>

      <div className="page-body">
        <div className="page-inner stack stagger">
          {error && <div className="banner banner--fault">Could not reach the console API: {error}</div>}

          <p className="page-lede">
            SABnzbd and the *arr libraries. Everything here opens the real thing.
          </p>

          {data && (
            <section className={`card dl-sum${flashing === "downloads" ? " is-flash" : ""}`} id="downloads">
              <div className="dl-sum__top">
                <div className="grow">
                  <div>
                    <span className="dl-sum__speed">{speed.n}</span>
                    <span className="dl-sum__unit">{speed.unit}</span>
                  </div>
                  <div className="dl-sum__meta">
                    {data.downloads.ok
                      ? `${data.downloads.sizeLeftText} left${data.downloads.timeLeftText ? ` · ${data.downloads.timeLeftText} remaining` : ""}`
                      : data.downloads.detail}
                  </div>
                </div>
                {data.downloads.paused ? (
                  <span className="pill pill--warn dl-sum__queued">Paused</span>
                ) : (
                  <span className="pill pill--grad dl-sum__queued">{data.downloads.queueCount} queued</span>
                )}
              </div>
              <div className={`meter ${data.downloads.ok ? "meter--live" : "meter--fault"}`}>
                <i
                  className={live ? "is-live" : undefined}
                  style={{ transform: `scaleX(${Math.min(100, Math.max(0, overall)) / 100})` }}
                />
              </div>
            </section>
          )}

          {jobs.length > 0 && (
            <Section id="jobs" title="Jobs" flashing={flashing}>
              {jobs.slice(0, 6).map((j) => (
                <a
                  key={j.id}
                  className="job job--go"
                  href={sabUrl}
                  target="_blank"
                  rel="noreferrer"
                  title="Open SABnzbd"
                >
                  <div className="job__title truncate">{j.title}</div>
                  <div className="meter meter--live">
                    <i
                      className={/download/i.test(j.status) ? "is-live" : undefined}
                      style={{ transform: `scaleX(${j.percent / 100})` }}
                    />
                  </div>
                  <div className="job__foot">
                    <span>{j.status}</span>
                    <span>
                      {Math.round(j.percent)}% · {etaMin(j.etaText)}
                    </span>
                  </div>
                </a>
              ))}
            </Section>
          )}

          <Section
            id="libraries"
            title="Libraries"
            flashing={flashing}
            aside={<span className="pill">{data?.queueCount ?? 0} in queue</span>}
          >
            {data?.libraries.map((l) => (
              <LibraryRow key={l.id} l={l} />
            ))}
          </Section>

          {data && (
            <div className="stamp">
              <span className="dot dot--ok dot--live" />
              Updated {new Date(data.generatedAt).toLocaleTimeString()} · {data.mode}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
